/** Reserved /org/:slug segments that are not organization slugs. */
export const RESERVED_ORG_SLUGS = new Set(['join']);

const SHADOW_ORG_PATH_RE = /^\/org\/([^/]+)(\/.*)?$/;

export function parseShadowOrgSlug(pathname: string): string | null {
  const match = pathname.match(SHADOW_ORG_PATH_RE);
  const slug = match?.[1];
  if (!slug || RESERVED_ORG_SLUGS.has(slug)) return null;
  return slug;
}

export function shadowPrefixForSlug(slug: string): string {
  return `/org/${slug}`;
}

/**
 * Where the tenant app lives on a host whose root is the club's own site.
 *
 * uchicago.chessdirector.com serves a hand-built club site at `/`, so the
 * platform sits under this prefix: `/events` is the tournament hub and
 * `/events/<id>` is one event. Recognised from the path alone — routing has
 * to know before the tenant config has finished loading.
 */
export const CLUB_APP_PREFIX = '/events';

export function parseClubAppPrefix(pathname: string): string | null {
  return pathname === CLUB_APP_PREFIX || pathname.startsWith(`${CLUB_APP_PREFIX}/`)
    ? CLUB_APP_PREFIX
    : null;
}

/**
 * Paths that move on a host that has its own club site, keyed by the host's
 * first label. Mirrors SITE_PATH_REDIRECTS in
 * server/middleware/clubStaticSite.js, which handles a direct hit; this one
 * covers in-app navigation, which never reaches the server.
 *
 * uchicago: the platform's global tournament list is not the club's events
 * page — the club's own schedule under /events is.
 */
const CLUB_SITE_PATH_REDIRECTS: Record<string, Record<string, string>> = {
  uchicago: {
    '/public/tournaments': `${CLUB_APP_PREFIX}/site/schedule`,
  },
};

/**
 * Where `pathname` should go on `hostname`, or null when it stays put. Only
 * the exact path moves: /public/tournaments/<id> is a real event page.
 */
export function clubSiteRedirectFor(hostname: string, pathname: string): string | null {
  const firstLabel = String(hostname || '').toLowerCase().split(':')[0].split('.')[0];
  const table = CLUB_SITE_PATH_REDIRECTS[firstLabel];
  if (!table) return null;
  // A trailing slash is the same page.
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return table[normalized] || null;
}

/** Prefix an in-app path when browsing under /org/:slug shadow tenant. */
export function prefixTenantPath(path: string, shadowPrefix: string | null): string {
  if (!shadowPrefix) return path;
  if (/^https?:\/\//i.test(path) || path.startsWith('mailto:')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === shadowPrefix || normalized.startsWith(`${shadowPrefix}/`)) return normalized;
  if (normalized === '/') return shadowPrefix;
  return `${shadowPrefix}${normalized}`;
}

/** Strip /org/:slug prefix for route matching (e.g. header active states). */
export function stripShadowPrefix(pathname: string, shadowPrefix: string | null): string {
  if (!shadowPrefix || !pathname.startsWith(shadowPrefix)) return pathname;
  const rest = pathname.slice(shadowPrefix.length) || '/';
  return rest.startsWith('/') ? rest : `/${rest}`;
}
